/**
 * Pantalla de acceso.
 *
 * El botón grande es "Jugar como invitado" y está arriba del todo, antes que el
 * formulario. No es una preferencia estética: pedir un correo antes de dejar
 * tocar el juego es donde se pierde a la mayoría de la gente que instala un
 * juego de móvil. La cuenta anónima es una cuenta de verdad —tiene perfil,
 * colección y mazo— y se puede convertir en permanente más tarde sin perder
 * nada, que es el momento en el que pedir el correo tiene sentido: cuando ya hay
 * algo que proteger.
 */

import { LogIn, Sparkles, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Button } from './Button';

interface AuthPanelProps {
  isBusy: boolean;
  error: string | null;
  onAnonymous: () => void;
  onSignIn: (email: string, password: string) => void;
  onSignUp: (email: string, password: string, username: string) => void;
}

type Mode = 'signin' | 'signup';

export function AuthPanel({ isBusy, error, onAnonymous, onSignIn, onSignUp }: AuthPanelProps) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');

  const canSubmit = email.trim().length > 3 && password.length >= 6 && !isBusy;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (mode === 'signin') onSignIn(email.trim(), password);
    else onSignUp(email.trim(), password, username.trim());
  };

  const fieldClass =
    'w-full rounded-lg border border-slate-600 bg-slate-800/80 px-3 py-2.5 text-sm text-slate-100 ' +
    'placeholder:text-slate-500 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 focus:outline-none';

  return (
    <div className="grid flex-1 place-items-center overflow-y-auto p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="bg-linear-to-r from-amber-300 to-rose-400 bg-clip-text text-4xl font-black tracking-tight text-transparent">
            TRÍADA
          </h1>
          <p className="mt-1 text-sm text-slate-400">Tres zonas. Diez turnos. Un ganador.</p>
        </div>

        <Button size="lg" className="w-full" onClick={onAnonymous} disabled={isBusy}>
          <Sparkles className="h-4 w-4" aria-hidden />
          Jugar como invitado
        </Button>
        <p className="mt-2 text-center text-[11px] leading-snug text-slate-500">
          Empiezas ya. Podrás añadir un correo después para conservar la colección.
        </p>

        <div className="my-5 flex items-center gap-3 text-[11px] tracking-widest text-slate-600 uppercase">
          <span className="h-px flex-1 bg-slate-700" />o<span className="h-px flex-1 bg-slate-700" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'signup' ? (
            <div>
              <label htmlFor="username" className="mb-1 block text-xs font-medium text-slate-400">
                Nombre de jugador
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Se genera uno si lo dejas vacío"
                autoComplete="username"
                maxLength={20}
                className={fieldClass}
              />
            </div>
          ) : null}

          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-medium text-slate-400">
              Correo
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              // inputMode ajusta el teclado del móvil: sin esto hay que ir a
              // buscar la arroba en la tercera pantalla de símbolos.
              inputMode="email"
              className={fieldClass}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-xs font-medium text-slate-400">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              className={fieldClass}
            />
            {mode === 'signup' ? (
              <p className="mt-1 text-[11px] text-slate-500">Mínimo 6 caracteres.</p>
            ) : null}
          </div>

          {error ? (
            <p
              role="alert"
              className="rounded-lg bg-rose-950/70 px-3 py-2 text-xs text-rose-200 ring-1 ring-rose-500/40"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="secondary" size="lg" className="w-full" disabled={!canSubmit}>
            {mode === 'signin' ? (
              <>
                <LogIn className="h-4 w-4" aria-hidden />
                Entrar
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" aria-hidden />
                Crear cuenta
              </>
            )}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
          className="mt-4 w-full text-center text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
        >
          {mode === 'signin' ? '¿No tienes cuenta? Crear una' : '¿Ya tienes cuenta? Entrar'}
        </button>
      </div>
    </div>
  );
}
